import { type ThreadMessage, useAuiState } from '@assistant-ui/react';
import { hashKey, useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { evalScript } from '#core/lib/expr/evaluator.ts';
import { type Recipe, recipeToForm } from '#core/lib/expr/session/bindingStore.ts';
import { useEvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { buildEvalContext } from '#core/lib/expr/session/useEvalExpr.ts';

import { recipesFromMessages } from '#plugin-chat/lib/transport/bindingProjection.ts';

/** Order-sensitive per-recipe identity (name + structural exprAst). */
const recipeKeys = (recipes: readonly Recipe[]): readonly string[] => recipes.map((r) => hashKey([r.name, r.exprAst]));

/** `prev` is a prefix of `next` (pure append — no entry changed). */
const isPrefix = (prev: readonly string[], next: readonly string[]): boolean =>
  prev.length <= next.length && prev.every((k, i) => k === next[i]);

/**
 * Reconciles the session's binding namespace to be a **projection of
 * the active message branch**. Bindings are no longer a separately
 * persisted log — the producing expressions already live in the
 * producer tool-call parts on the branch (`recipesFromMessages`), so
 * there is one source of truth (the transcript) and no drift.
 *
 * Children must not render until the projection is consistent —
 * otherwise a tile could resolve `(col <name> …)` against a half-built
 * namespace. The caller wraps this in `<Suspense>` (fallback while the
 * gate returns `null`).
 *
 * Three cases, one mechanism:
 *
 * - **Reload / thread-switch** — first run for a `session.id` (or a new
 *   one). The persisted active branch is loaded by the runtime into
 *   `thread.messages`; the query replays its recipes into the fresh
 *   (empty) store. `resetBindings()` is a no-op here.
 * - **Branch growth (a producer completes mid-turn)** — the active
 *   branch is a pure prefix-extension; the new binding was already
 *   recorded by the live `recordBinding` commit path, so the query key
 *   is left unchanged: no refetch, no unmount, no flicker, no redundant
 *   replay.
 * - **Branch divergence (regenerate / edit / branch-switch)** — the
 *   recipe sequence is no longer a prefix-extension. Bump the epoch so
 *   the query re-runs: it `resetBindings()` (drops the abandoned
 *   branch's recipes — the sole cause of the spurious
 *   `SessionRebindingError`) then replays the surviving branch.
 *
 * Replay is strictly sequential (`await`) because a later recipe may
 * reference an earlier binding. A per-recipe failure is logged and
 * skipped so one bad recipe never wipes the conversation.
 */
export function SessionHydrationGate({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const deps = useEvalDeps();
  const messages = useAuiState(({ thread }) => thread.messages) as readonly ThreadMessage[];

  const recipes = recipesFromMessages(messages);
  const keys = recipeKeys(recipes);

  // Identity-stable reconciliation in render (the React-idiomatic
  // "adjust state when inputs change during render" pattern — NOT an
  // effect; React 19 + React Compiler), mirroring `useChatSession`.
  // `epoch` is the only thing the query key tracks, and it advances
  // *only* on a genuine branch divergence — growth never changes it.
  const [state, setState] = useState(() => ({ sessionId: session.id, projectedKeys: keys, epoch: 0 }));

  let { epoch } = state;
  if (state.sessionId !== session.id) {
    // Thread switch: new namespace, project this thread's branch.
    setState({ sessionId: session.id, projectedKeys: keys, epoch: 0 });
    epoch = 0;
  } else if (isPrefix(state.projectedKeys, keys)) {
    // Same branch. Pure growth (a producer completed mid-turn) only
    // needs the tracked keys advanced — the binding was already
    // recorded live, so the query key stays put: no refetch/unmount.
    if (keys.length !== state.projectedKeys.length) {
      setState({ ...state, projectedKeys: keys });
    }
  } else {
    // Divergence (regenerate / edit / branch-switch): an earlier
    // recipe changed or vanished. Bump the epoch so the query re-runs,
    // resets, and re-projects the surviving branch.
    epoch = state.epoch + 1;
    setState({ sessionId: session.id, projectedKeys: keys, epoch });
  }

  const ready = useQuery<true>({
    queryKey: ['session-hydrate', session.id, epoch] as const,
    queryFn: async (): Promise<true> => {
      // Idempotent on the initial run (empty store); on a divergence it
      // drops the abandoned branch's recipes before re-projecting.
      session.resetBindings();
      const ctx = buildEvalContext(session, deps);
      for (const recipe of recipes) {
        try {
          // Sequential by design: a recipe may reference an earlier
          // binding, so each must be bound before the next replays.
          await evalScript([recipeToForm(recipe)], new Map(), ctx, deps.ops, deps.verbs);
        } catch (err) {
          console.error('session hydration: skipping recipe', recipe.name, err);
        }
      }
      return true;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (!ready.data) {
    return null;
  }

  return <>{children}</>;
}
